import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { TranslateButton } from '../../components/admin/TranslateButton';
import { 
  Save, 
  Loader2,
  ArrowLeft,
  FileText,
  Image,
  Type,
  Link as LinkIcon,
  Info,
  Eye,
  RefreshCw,
  Plus,
  Trash2,
  GripVertical,
  ImagePlus,
  X,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

const sectionLabels = {
  jugar: 'Jugar',
  iniciacio: 'Iniciació / Escola',
  drivingRange: 'Camp de Pràctiques',
  restaurant: 'Restaurant',
  allotjament: 'Allotjament',
  correspondencies: 'Correspondències',
  equip: 'Equip'
};

export const AdminSectionEditor = () => {
  const { sectionKey } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState('ca');
  const [pendingTranslations, setPendingTranslations] = useState([]); // Track languages with unsaved translations
  
  // Shared gallery state (same for all languages)
  const [sharedGallery, setSharedGallery] = useState([]);
  
  // Content state for all languages (gallery removed - now shared)
  const [content, setContent] = useState({
    ca: { hero_image: '', title: '', intro: '', cta_text: '', cta_link: '' },
    es: { hero_image: '', title: '', intro: '', cta_text: '', cta_link: '' },
    fr: { hero_image: '', title: '', intro: '', cta_text: '', cta_link: '' },
    en: { hero_image: '', title: '', intro: '', cta_text: '', cta_link: '' }
  });

  // Handle translation callback
  const handleTranslation = (field, translations, translatedLanguages) => {
    if (!translations) return;
    
    // Update content for all translated languages
    setContent(prev => {
      const newContent = { ...prev };
      Object.entries(translations).forEach(([lang, value]) => {
        if (newContent[lang]) {
          newContent[lang] = {
            ...newContent[lang],
            [field]: value
          };
        }
      });
      return newContent;
    });
    
    // Mark these languages as having pending translations
    setPendingTranslations(prev => {
      const unique = new Set([...prev, ...(translatedLanguages || [])]);
      return Array.from(unique);
    });
  };

  // Clear pending translations marker for a language after save
  const clearPendingTranslation = (lang) => {
    setPendingTranslations(prev => prev.filter(l => l !== lang));
  };

  useEffect(() => {
    if (!sectionKey) {
      navigate('/admin/sections');
      return;
    }
    fetchContent();
  }, [sectionKey, navigate]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      // Fetch content for all languages in parallel with cache-busting
      const cacheBuster = Date.now();
      const responses = await Promise.all(
        languages.map(lang => 
          authFetch(`${API_URL}/api/section-content/${sectionKey}/${lang.code}?_t=${cacheBuster}`)
        )
      );
      
      const newContent = { ...content };
      let galleryLoaded = false;
      
      for (let i = 0; i < responses.length; i++) {
        if (responses[i].ok) {
          const data = await responses[i].json();
          newContent[languages[i].code] = {
            hero_image: data.hero_image || '',
            title: data.title || '',
            intro: data.intro || '',
            cta_text: data.cta_text || '',
            cta_link: data.cta_link || ''
          };
          
          // Load shared gallery from first language that has it (gallery is shared across all)
          if (!galleryLoaded && data.gallery && data.gallery.length > 0) {
            setSharedGallery(data.gallery);
            galleryLoaded = true;
          }
        }
      }
      
      setContent(newContent);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Error carregant contingut');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save content for current language with shared gallery
      const currentContent = content[activeLang];
      
      const response = await authFetch(`${API_URL}/api/admin/section-content/${sectionKey}/${activeLang}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hero_image: currentContent.hero_image,
          title: currentContent.title,
          intro: currentContent.intro,
          cta_text: currentContent.cta_text,
          cta_link: currentContent.cta_link,
          gallery: sharedGallery // Use shared gallery
        })
      });

      if (response.ok) {
        toast.success(`Contingut guardat (${activeLang.toUpperCase()})`);
        clearPendingTranslation(activeLang);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant contingut');
      }
    } catch (error) {
      toast.error('Error guardant contingut');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      let successCount = 0;
      
      for (const lang of languages) {
        const langContent = content[lang.code];
        
        const response = await authFetch(`${API_URL}/api/admin/section-content/${sectionKey}/${lang.code}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hero_image: langContent.hero_image,
            title: langContent.title,
            intro: langContent.intro,
            cta_text: langContent.cta_text,
            cta_link: langContent.cta_link,
            gallery: sharedGallery // Use shared gallery for all languages
          })
        });

        if (response.ok) {
          successCount++;
        }
      }
      
      toast.success(`Contingut guardat en ${successCount} idiomes (galeria sincronitzada)`);
      setPendingTranslations([]); // Clear all pending translations
    } catch (error) {
      toast.error('Error guardant contingut');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({
      ...prev,
      [activeLang]: {
        ...prev[activeLang],
        [field]: value
      }
    }));
  };

  // Copy hero image to all languages
  const copyHeroToAllLanguages = () => {
    const heroImage = content[activeLang].hero_image;
    setContent(prev => {
      const newContent = { ...prev };
      languages.forEach(lang => {
        newContent[lang.code] = {
          ...newContent[lang.code],
          hero_image: heroImage
        };
      });
      return newContent;
    });
    toast.success('Imatge copiada a tots els idiomes');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  const currentContent = content[activeLang];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-section-editor">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/sections')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Tornar
            </Button>
            <div>
              <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
                <FileText className="w-6 h-6" />
                {sectionLabels[sectionKey] || sectionKey}
              </h1>
              <p className="text-[#666666] mt-1">
                Edita el contingut d'aquesta secció
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <a
              href={`/${sectionKey === 'drivingRange' ? 'driving-range' : sectionKey}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] rounded text-sm text-[#666666] hover:border-[#1A4D2E] hover:text-[#1A4D2E] transition-colors"
            >
              <Eye className="w-4 h-4" />
              Vista prèvia
            </a>
          </div>
        </div>

        {/* Info banner about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Format d'imatges recomanat:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Imatge hero:</strong> 1920×700 px, format JPG/WebP, màx. 300KB</li>
                <li>Puja les imatges a un servei com <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="underline">Imgur</a> o utilitza URLs directes</li>
                <li>La imatge hero és compartida per tots els idiomes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Language selector */}
        <div className="flex gap-2 bg-[#F5F5F5] p-1 rounded-lg w-fit">
          {languages.map((l) => {
            const hasPending = pendingTranslations.includes(l.code);
            return (
              <button
                key={l.code}
                onClick={() => setActiveLang(l.code)}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors relative ${
                  l.code === activeLang 
                    ? 'bg-white text-[#1A4D2E] shadow-sm' 
                    : 'text-[#666666] hover:text-[#333333]'
                } ${hasPending ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
              >
                {l.flag} {l.name}
                {hasPending && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" 
                        title="Traduccions pendents de guardar" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Pending translations indicator */}
        {pendingTranslations.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
            <p className="text-sm text-amber-800">
              Hi ha traduccions pendents de guardar. Revisa cada idioma i clica "Guardar" per aplicar els canvis.
            </p>
          </div>
        )}

        {/* Hero Image Section (Admin only) */}
        {isAdmin && (
          <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#333333] flex items-center gap-2">
                <Image className="w-4 h-4" />
                Imatge Hero (capçalera)
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={copyHeroToAllLanguages}
                className="gap-2 text-xs"
              >
                <RefreshCw className="w-3 h-3" />
                Copiar a tots els idiomes
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>URL de la imatge (1920×700 px recomanat)</Label>
              <Input
                value={currentContent.hero_image}
                onChange={(e) => updateField('hero_image', e.target.value)}
                placeholder="https://..."
                data-testid="section-hero-image"
              />
              {currentContent.hero_image && (
                <img 
                  src={currentContent.hero_image} 
                  alt="Hero preview" 
                  className="mt-2 max-h-32 rounded border object-cover"
                />
              )}
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#333333] flex items-center gap-2">
              <Type className="w-4 h-4" />
              Contingut ({activeLang.toUpperCase()})
            </h3>
            <TranslateButton
              text={`${currentContent.title || ''}\n\n${currentContent.intro || ''}`}
              sourceLanguage={activeLang}
              onTranslated={(translations, langs) => {
                // Split translations back into title and intro
                Object.entries(translations).forEach(([lang, text]) => {
                  const parts = text.split('\n\n');
                  const title = parts[0] || '';
                  const intro = parts.slice(1).join('\n\n') || '';
                  
                  setContent(prev => ({
                    ...prev,
                    [lang]: {
                      ...prev[lang],
                      title,
                      intro
                    }
                  }));
                });
                setPendingTranslations(prev => {
                  const unique = new Set([...prev, ...(langs || [])]);
                  return Array.from(unique);
                });
              }}
              onLanguageSwitch={(lang) => setActiveLang(lang)}
              showFullLabel
            />
          </div>
          
          <div className="space-y-2">
            <Label>Títol de la pàgina</Label>
            <Input
              value={currentContent.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="Ex: Jugar"
              data-testid="section-title"
            />
          </div>

          <div className="space-y-2">
            <Label>Contingut principal</Label>
            <RichTextEditor
              value={currentContent.intro}
              onChange={(value) => updateField('intro', value)}
              placeholder="Escriu el contingut de la secció..."
              minHeight={200}
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-[#333333] flex items-center gap-2">
            <LinkIcon className="w-4 h-4" />
            Botó d'acció (CTA)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Text del botó ({activeLang.toUpperCase()})</Label>
              <Input
                value={currentContent.cta_text}
                onChange={(e) => updateField('cta_text', e.target.value)}
                placeholder="Ex: Reserva ara"
                data-testid="section-cta-text"
              />
            </div>
            <div className="space-y-2">
              <Label>Enllaç del botó</Label>
              <Input
                value={currentContent.cta_link}
                onChange={(e) => updateField('cta_link', e.target.value)}
                placeholder="/contacte o https://..."
                data-testid="section-cta-link"
              />
            </div>
          </div>
        </div>

        {/* Image Gallery Section */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#333333] flex items-center gap-2">
              <ImagePlus className="w-4 h-4" />
              Galeria d'imatges (compartida per tots els idiomes)
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const newGallery = [...sharedGallery, { 
                  id: Date.now().toString(),
                  url: '', 
                  caption: '',
                  order: sharedGallery.length 
                }];
                setSharedGallery(newGallery);
              }}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Afegir imatge
            </Button>
          </div>

          {/* Size recommendations */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Mides recomanades
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Galeria:</strong> 800×600 px (format 4:3) - màx. 300KB</li>
              <li>• <strong>Format:</strong> JPG o WebP per millor compressió</li>
              <li>• <strong>Consell:</strong> Utilitza imatges de qualitat similar per mantenir coherència visual</li>
            </ul>
          </div>

          {/* Gallery images */}
          {sharedGallery && sharedGallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedGallery.map((img, index) => (
                <div 
                  key={img.id || index} 
                  className="border border-[#E5E5E5] rounded-lg p-4 space-y-3 bg-[#FAFAFA]"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#666666]">
                      Imatge {index + 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newGallery = sharedGallery.filter((_, i) => i !== index);
                        setSharedGallery(newGallery);
                      }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Image URL input */}
                  <div className="space-y-2">
                    <Label className="text-xs">URL de la imatge</Label>
                    <Input
                      value={img.url || ''}
                      onChange={(e) => {
                        const newGallery = [...sharedGallery];
                        newGallery[index] = { ...newGallery[index], url: e.target.value };
                        setSharedGallery(newGallery);
                      }}
                      placeholder="https://..."
                      className="text-sm"
                    />
                  </div>

                  {/* Image preview */}
                  {img.url && (
                    <div className="relative aspect-[4/3] bg-[#E5E5E5] rounded overflow-hidden">
                      <img 
                        src={img.url} 
                        alt={`Galeria ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Caption input */}
                  <div className="space-y-2">
                    <Label className="text-xs">Peu de foto - opcional</Label>
                    <Input
                      value={img.caption || ''}
                      onChange={(e) => {
                        const newGallery = [...sharedGallery];
                        newGallery[index] = { ...newGallery[index], caption: e.target.value };
                        setSharedGallery(newGallery);
                      }}
                      placeholder="Descripció breu..."
                      className="text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#888888]">
              <ImagePlus className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hi ha imatges a la galeria</p>
              <p className="text-sm">Clica "Afegir imatge" per començar</p>
            </div>
          )}
        </div>

        {/* Save buttons */}
        <div className="flex justify-between items-center sticky bottom-16 bg-white py-4 mt-6 border-t border-[#E5E5E5] -mx-6 px-6 lg:-mx-8 lg:px-8 shadow-lg rounded-lg z-10">
          <Button
            variant="outline"
            onClick={fetchContent}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Recarregar
          </Button>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar ({activeLang.toUpperCase()})
            </Button>
            
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
              data-testid="save-all-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Guardar tots els idiomes
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSectionEditor;
